// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Survey type colours (frontend-r22.23): the colour a type wears under the
 * Ressourcer "Materiale" lens — on the Ground, its strip card, the Tabel and
 * the legend.
 *
 * Every colour is one slot of a categorical palette, `--type-1` …
 * `--type-11` in `tokens.css` (stepped per theme, colour-blind checked; see
 * the comment there). A type that has no colour of its own takes an *auto*
 * slot from its id (`autoSlot`): a function of the id alone, so a new type
 * never repaints the others, and consecutive ids — types created together —
 * land on neighbouring slots, which are the pairs the palette is validated
 * on. Only the first `AUTO_SLOTS` take part in the rotation; the rest are
 * offered in the picker only.
 *
 * A chosen colour is stored as the slot's light-theme hex (`#rrggbb`) —
 * `SurveyType.color` on a server that keeps it (`PATCH /survey/types/{id}
 * {color}`), else in localStorage per case (`typeColorStorageKey`) — and is
 * drawn through the slot's token, so it follows the theme. A hex of the
 * first palette (`LEGACY_HEX`) keeps its slot; any other hex from outside the
 * palette (another client) is drawn as its nearest slot.
 *
 * Pure: no React, no DOM (storage is passed in).
 */

import type { SurveyType } from '../api/types';

export interface TypeColorSlot {
  /** 1-based slot number: the token is `--type-<n>`. */
  n: number;
  /** The token name. */
  token: string;
  /** The light-theme value: what is stored. Mirrors tokens.css. */
  hex: string;
  /** The picker's name for it. */
  label: string;
}

const SLOT_DEFS: readonly [string, string][] = [
  ['#e58871', 'Terrakotta'],
  ['#0a8063', 'Havgrøn'],
  ['#c09d3d', 'Okker'],
  ['#ae5f89', 'Rosa'],
  ['#61af71', 'Grøn'],
  ['#8f86d7', 'Lavendel'],
  ['#9c4245', 'Teglrød'],
  ['#2cb7d4', 'Turkis'],
  ['#898821', 'Oliven'],
  ['#7d4b92', 'Blomme'],
  ['#8c5f00', 'Brun'],
];

/**
 * The first palette's light hexes (frontend-r22.23), slot for slot. A type
 * whose colour was stored before the palette was re-derived from the theme
 * (frontend-r22.25) keeps its slot instead of snapping to whatever new slot
 * is nearest.
 */
const LEGACY_HEX: readonly string[] = [
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#b48ae0',
  '#e34948',
  '#2fb3c9',
  '#7d8a00',
  '#a83f8a',
  '#9a6324',
];

/** The palette, in rotation order (mirrors `--type-N` in tokens.css). */
export const TYPE_PALETTE: readonly TypeColorSlot[] = SLOT_DEFS.map(([hex, label], i) => ({
  n: i + 1,
  token: `--type-${i + 1}`,
  hex,
  label,
}));

/** How many leading slots the automatic assignment cycles through. */
export const AUTO_SLOTS = 10;

/** The auto slot of a type id (0-based index into `TYPE_PALETTE`). */
export function autoSlot(id: number): number {
  const i = Math.trunc(id);
  return (((i - 1) % AUTO_SLOTS) + AUTO_SLOTS) % AUTO_SLOTS;
}

const HEX = /^#([0-9a-f]{6})$/i;

/** `#RRGGBB` / `#rrggbb` → lower case; anything else → null. */
export function normalizeHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const m = HEX.exec(value.trim());
  return m ? `#${m[1].toLowerCase()}` : null;
}

function rgbOf(hex: string): [number, number, number] {
  const h = hex.slice(1);
  return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** The slot index a stored colour is drawn as: exact match (current or legacy palette), else the nearest (rgb distance); null for no colour. */
export function slotOfHex(value: string | null | undefined): number | null {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const exact = TYPE_PALETTE.findIndex((s) => s.hex === hex);
  if (exact >= 0) return exact;
  const legacy = LEGACY_HEX.indexOf(hex);
  if (legacy >= 0) return legacy;
  const [r, g, b] = rgbOf(hex);
  let best = 0;
  let bestD = Infinity;
  TYPE_PALETTE.forEach((s, i) => {
    const [sr, sg, sb] = rgbOf(s.hex);
    const d = (r - sr) ** 2 + (g - sg) ** 2 + (b - sb) ** 2;
    if (d < bestD) [best, bestD] = [i, d];
  });
  return best;
}

/** The slot a type is drawn in: its chosen colour, else its auto slot. */
export function typeSlot(type: Pick<SurveyType, 'id' | 'color'>): number {
  return slotOfHex(type.color) ?? autoSlot(type.id);
}

/** The token a type is drawn in under the Materiale lens. */
export function typeColorToken(type: Pick<SurveyType, 'id' | 'color'>): string {
  return TYPE_PALETTE[typeSlot(type)].token;
}

/** Whether the type wears a colour someone chose (not the automatic one). */
export function hasChosenColor(type: Pick<SurveyType, 'color'>): boolean {
  return normalizeHex(type.color) !== null;
}

// ------------------------------------------------------- server support --

/**
 * Whether the server keeps `SurveyType.color`: it then sends the key on
 * every type (null when unset). An older ruxd omits it — the colours are
 * kept in localStorage instead. Unknown (no types yet) reads as false.
 */
export function tracksTypeColor(types: readonly SurveyType[]): boolean {
  return types.some((t) => 'color' in t && t.color !== undefined);
}

/** typeId → stored hex. */
export type TypeColorMap = Record<string, string>;

/**
 * The types with their local colours folded in, for a server that does not
 * keep them (`supported` false). With `supported`, the server's value is the
 * only copy and the list is returned as is.
 */
export function withLocalColors(types: SurveyType[], local: TypeColorMap, supported: boolean): SurveyType[] {
  if (supported || Object.keys(local).length === 0) return types;
  return types.map((t) => {
    const hex = normalizeHex(local[String(t.id)]);
    return hex ? { ...t, color: hex } : t;
  });
}

// ---------------------------------------------------------------- storage --

/** The localStorage key prefix; the full key is `reusex:type-colors-v1:<caseId>`. */
export const TYPE_COLOR_STORAGE_PREFIX = 'reusex:type-colors-v1:';

export function typeColorStorageKey(caseId: string): string {
  return `${TYPE_COLOR_STORAGE_PREFIX}${caseId}`;
}

export type TypeColorStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** A stored map, keeping only integer ids with a valid hex; garbage reads as empty. */
export function parseTypeColors(raw: string | null): TypeColorMap {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const out: TypeColorMap = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    const hex = normalizeHex(v);
    if (/^\d+$/.test(k) && hex) out[k] = hex;
  }
  return out;
}

export function readTypeColors(store: TypeColorStore | undefined, caseId: string | undefined): TypeColorMap {
  if (!store || !caseId) return {};
  try {
    return parseTypeColors(store.getItem(typeColorStorageKey(caseId)));
  } catch {
    return {};
  }
}

/** The map with one type's colour set (hex) or cleared (null). */
export function setTypeColorIn(map: TypeColorMap, typeId: number, color: string | null): TypeColorMap {
  const next = { ...map };
  const hex = normalizeHex(color);
  if (hex) next[String(typeId)] = hex;
  else delete next[String(typeId)];
  return next;
}

/** Writes the local copy; an empty map removes the key. Storage failures are swallowed. */
export function writeTypeColors(store: TypeColorStore | undefined, caseId: string | undefined, map: TypeColorMap): void {
  if (!store || !caseId) return;
  try {
    const key = typeColorStorageKey(caseId);
    if (Object.keys(map).length === 0) store.removeItem(key);
    else store.setItem(key, JSON.stringify(map));
  } catch {
    // Private mode / quota / denied — the colour simply will not persist.
  }
}
